import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardContent, Avatar, Typography, Button, Grid, Divider, Chip,
  Stack, Skeleton, Tooltip, List, ListItem, ListItemText, Box
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import EditIcon from "@mui/icons-material/Edit";
import LogoutIcon from "@mui/icons-material/Logout";
import ShieldIcon from "@mui/icons-material/Shield";
import { onAuthStateChanged, signOut, getIdTokenResult } from "firebase/auth";
import { auth } from "../firebase";

// OPTIONAL: if you store profile details in Firestore
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
const db = getFirestore();

function initials(name = "", email = "") {
  const n = name?.trim();
  if (n) {
    const parts = n.split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase()).join("");
  }
  return (email?.[0] || "?").toUpperCase();
}

function fmtDate(ts) {
  try {
    const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

export default function Profile() {
  const [authUser, setAuthUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);

  // App profile (Firestore): { tier, recentActivity, watchlist, phone, timezone, mfaEnabled, ... }
  const [appProfile, setAppProfile] = useState(null);

  // Backend subscription (Stripe) example: { plan, status, renewsAt }
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setLoading(true);
      try {
        setAuthUser(u);
        if (u) {
          const tokenRes = await getIdTokenResult(u);
          setClaims(tokenRes?.claims || null);

          // ---- Firestore user profile (OPTIONAL) ----
          // Expect a doc at users/{uid} with fields you control.
          const userRef = doc(db, "users", u.uid);
          const stop = onSnapshot(userRef, (snap) => {
            setAppProfile(snap.exists() ? snap.data() : {});
          });

          // ---- Backend subscription (OPTIONAL) ----
          // Replace with your endpoint that returns Stripe data for this uid.
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/stripe/subscription?uid=${u.uid}`, {
            credentials: "include",
          })
            .then(r => (r.ok ? r.json() : null))
            .then(data => setSubscription(data))
            .catch(() => setSubscription(null));

          // Cleanup Firestore listener when user changes/unmounts
          return () => stop();
        } else {
          setAppProfile(null);
          setSubscription(null);
        }
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const name = useMemo(() => {
    return authUser?.displayName || appProfile?.name || "New User";
  }, [authUser, appProfile]);

  const email = authUser?.email || appProfile?.email || "";
  const emailVerified = !!authUser?.emailVerified;
  const memberSince = authUser?.metadata?.creationTime || appProfile?.createdAt;
  const lastSignIn = authUser?.metadata?.lastSignInTime || appProfile?.lastLoginAt;
  const photoURL = authUser?.photoURL || appProfile?.photoURL || "";
  const tier =
    appProfile?.tier ||
    claims?.plan || // e.g., custom claim set by backend
    subscription?.plan ||
    "Free";

  const mfaEnabled = appProfile?.mfaEnabled ?? claims?.mfa ?? false;
  const timezone = appProfile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const recentActivity = appProfile?.recentActivity || [];
  const watchlist = appProfile?.watchlist || [];

  const handleLogout = async () => {
    await signOut(auth);
    // redirect if desired
    // window.location.assign("/login");
  };

  const handleEditProfile = () => {
    // route to your profile edit page / dialog
    // e.g., navigate("/settings/profile");
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4, px: 2 }}>
      <Grid item xs={12} md={8} lg={7}>
        <Card>
          <CardContent>
            {/* Header */}
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                {loading ? (
                  <Skeleton variant="circular" width={64} height={64} />
                ) : photoURL ? (
                  <Avatar sx={{ width: 64, height: 64 }} src={photoURL} />
                ) : (
                  <Avatar sx={{ width: 64, height: 64 }}>{initials(name, email)}</Avatar>
                )}
              </Grid>

              <Grid item xs>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h5">
                    {loading ? <Skeleton width={160} /> : name}
                  </Typography>
                  {!loading && emailVerified && (
                    <Tooltip title="Email verified">
                      <VerifiedIcon fontSize="small" color="primary" />
                    </Tooltip>
                  )}
                  {!loading && mfaEnabled && (
                    <Tooltip title="Multi-factor enabled">
                      <ShieldIcon fontSize="small" color="action" />
                    </Tooltip>
                  )}
                </Stack>
                <Typography color="text.secondary">
                  {loading ? <Skeleton width={220} /> : email || "—"}
                </Typography>
              </Grid>

              <Grid item>
                {loading ? (
                  <Skeleton width={80} height={32} />
                ) : (
                  <Chip label={tier} color={tier === "Free" ? "default" : "primary"} />
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Account details */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Account</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Member since"
                      secondary={loading ? <Skeleton width={140} /> : fmtDate(memberSince)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Last sign-in"
                      secondary={loading ? <Skeleton width={180} /> : fmtDate(lastSignIn)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Timezone"
                      secondary={loading ? <Skeleton width={120} /> : timezone}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Email status"
                      secondary={
                        loading ? <Skeleton width={120} /> : emailVerified ? "Verified" : "Not verified"
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="MFA"
                      secondary={loading ? <Skeleton width={80} /> : mfaEnabled ? "Enabled" : "Disabled"}
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Subscription</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Plan"
                      secondary={loading ? <Skeleton width={100} /> : (subscription?.plan || tier)}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Status"
                      secondary={loading ? <Skeleton width={100} /> : (subscription?.status || "—")}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Renews / Expires"
                      secondary={loading ? <Skeleton width={160} /> : (subscription?.renewsAt ? fmtDate(subscription.renewsAt) : "—")}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Activity */}
            <Typography variant="h6" gutterBottom>Recent Activity</Typography>
            {loading ? (
              <Stack direction="row" spacing={1}>
                <Skeleton width={60} height={32} />
                <Skeleton width={60} height={32} />
                <Skeleton width={60} height={32} />
              </Stack>
            ) : recentActivity?.length ? (
              <Grid container spacing={1}>
                {recentActivity.slice(0, 12).map((s) => (
                  <Grid item key={s}>
                    <Chip label={s} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">No recent activity yet.</Typography>
            )}

            {/* Watchlist / Preferences */}
            {watchlist?.length ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Watchlist</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {watchlist.map((s) => <Chip key={s} label={s} variant="outlined" />)}
                </Box>
              </>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditProfile}>
                Edit Profile
              </Button>
              <Button variant="outlined" color="error" startIcon={<LogoutIcon />} onClick={handleLogout}>
                Log Out
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
